import { useEffect, useRef, useState, type Ref } from "react";
import type { GuestReview } from "@/hooks/use-guest-reviews";

export type ReviewCardProps = {
  review: GuestReview;
  index: number;
  expanded: boolean;
  onToggle: () => void;
  variant?: "carousel" | "static" | "property";
  clone?: boolean;
  cardRef?: Ref<HTMLElement>;
  onSelect?: (index: number, trigger: HTMLButtonElement) => void;
};

export function ReviewStars({ rating }: { rating: number }) {
  const wholeStars = Math.max(0, Math.min(5, Math.round(rating)));
  return <div className="flex gap-1 text-[#c98a45]" aria-label={`${rating} out of 5 stars`} role="img">
    {Array.from({ length: 5 }, (_, index) => <span key={index} aria-hidden="true" className={index < wholeStars ? "" : "opacity-30"}>★</span>)}
  </div>;
}

export default function ReviewCard({
  review,
  index,
  clone = false,
  expanded,
  onToggle,
  cardRef,
  variant = "carousel",
  onSelect,
}: ReviewCardProps) {
  const copyRef = useRef<HTMLQuoteElement>(null);
  const [needsToggle, setNeedsToggle] = useState(false);
  const isStatic = variant === "static";
  const isProperty = variant === "property";

  useEffect(() => {
    const element = copyRef.current;
    if (!element) return;
    const measure = () => {
      if (expanded) return;
      const lineHeight = parseFloat(window.getComputedStyle(element).lineHeight);
      setNeedsToggle(element.scrollHeight > (isProperty ? 3 : 5) * lineHeight + 1);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [expanded, isProperty, review.text]);

  const initial = review.authorName.trim().charAt(0).toUpperCase() || "G";
  const metadata = review.relativeTime
    ? `Google · ${review.relativeTime}`
    : review.date
      ? `Google · ${new Date(review.date).toLocaleDateString("en-NZ", { month: "short", year: "numeric" })}`
      : "Google";

  const cardClass = isProperty
    ? "flex w-[280px] shrink-0 flex-col p-5 sm:w-[340px] sm:p-6"
    : isStatic
    ? "flex min-w-0 flex-col py-1"
    : "flex min-h-[340px] w-[280px] shrink-0 flex-col p-5 sm:w-[340px] sm:p-6";
  const author = <span className="flex items-center gap-3 text-left">
    <span className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full bg-[#8b5025] text-sm font-bold text-white">
      {review.profilePhotoUrl ? <img src={review.profilePhotoUrl} alt="" className="h-full w-full object-cover" loading="lazy" referrerPolicy="no-referrer" /> : initial}
    </span>
    <span className="min-w-0">
      <strong className="block truncate text-[15px] font-normal text-white">{review.authorName}</strong>
      <span className="block pt-0.5 text-xs text-[color:var(--primary-foreground)]">{metadata}</span>
    </span>
  </span>;

  return <figure
    ref={cardRef}
    aria-hidden={clone || undefined}
    className={cardClass}
  >
    {variant === "carousel" && <figcaption className="mb-4">{author}</figcaption>}
    {isProperty && <figcaption className="mb-4">{onSelect ? <button type="button" onClick={(event) => onSelect(index, event.currentTarget)} tabIndex={clone ? -1 : 0} aria-label={`Open full review from ${review.authorName}`} className="rounded-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#AD6433]">{author}</button> : author}</figcaption>}
    <ReviewStars rating={review.rating} />
    <blockquote ref={copyRef} className={`${isProperty ? "mt-3" : "mt-5 min-h-[120px]"} shrink-0 text-[15px] leading-6 ${expanded ? "" : isProperty ? "line-clamp-3" : "line-clamp-5"} text-[color:var(--primary-foreground)]`} style={{ lineHeight: "24px" }}>{review.text}</blockquote>
    {needsToggle ? <button type="button" onClick={(event) => { event.stopPropagation(); onToggle(); }} tabIndex={clone ? -1 : 0} className="mt-3 min-h-11 shrink-0 self-start text-xs uppercase tracking-[0.14em] text-[#AD6433] underline underline-offset-4 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#AD6433]">
      {isStatic ? (expanded ? "Read less" : "Read more") : (expanded ? "Show less" : "Show more")}
    </button> : !isProperty && <div aria-hidden="true" data-review-toggle-spacer className="mt-3 h-11 shrink-0" />}
    {isStatic && <figcaption className="mt-3 flex items-center gap-3">{author}</figcaption>}
  </figure>;
}